import { createWebHistory, createRouter } from 'vue-router';
import { getTokens } from '../utils/auth';
import { useUsersStore } from '@/stores/useUsers';
import { currentSession } from '@/utils/auth';
import { hasAccess } from '@/composables/useNavigation';

import isFeatureFlagEnabled from '@/utils/featureFlags.js';
import { useEnvironment } from '@/composables/useEnvironment';

const publicRoutes = ['/login', '/notFound', '/notfound', '/unauthorized', '/forbidden'];

async function validateAuth(to) {
  if (publicRoutes.includes(to.path)) return true;

  const id_token = sessionStorage.getItem('id_token');
  if (!id_token || id_token === 'undefined') {
    if (import.meta.env.VITE_LOCAL_AUTH === 'true' && import.meta.env.MODE === 'desk') {
      return { name: 'login' };
    }
    return { name: 'unauthorized' };
  }
}

async function validatePermission(to) {
  try {
    // Check feature flag from route meta
    if (to.meta?.featureFlag) {
      const featureFlagPass = isFeatureFlagEnabled(to.meta.featureFlag);
      if (!featureFlagPass) return { name: 'lead' };
    }

    // Find allowedRoles from the current route or nearest parent
    let allowedRoles = to.meta?.allowedRoles;
    if (!allowedRoles) {
      for (const matched of to.matched) {
        if (matched.meta?.allowedRoles) {
          allowedRoles = matched.meta.allowedRoles;
          break;
        }
      }
    }

    const isAllowed = hasAccess(to.meta?.requiredSubscription, allowedRoles);
    if (!isAllowed) return { name: 'lead' };

    return true;
  } catch (err) {
    console.warn('Permission check failed:', err);
    return false;
  }
}

async function validateUserProfileAccess(to) {
  let users_store = useUsersStore();
  let currentUser = users_store.activeUser;
  let roles =
    currentUser['cognito:groups'] ||
    // handles page refresh scenario
    (() => {
      const token = currentSession();
      return token['cognito:groups'];
    })();

  // Check if 'SUP' or 'MGR' in roles
  if (roles.includes('SUP') || roles.includes('MGR')) {
    return true;
  }

  // Check if contributor is viewing their own profile
  let routeId = Number(to.params.auxo_user_id);
  let currentUserId = Number(currentUser.auxo_user_id);

  if (routeId !== currentUserId) {
    return { name: 'forbidden' };
  }

  return true;
}

function validateNonProd() {
  if (useEnvironment().isProd()) {
    return { name: 'notfound' };
  }
}

// Send Prod users to 404 not found page if they try to access hosted UI
function useHostedUI() {
  if (import.meta.env.VITE_LOCAL_AUTH === 'true' && import.meta.env.MODE === 'desk' && !useEnvironment().isProd()) {
    return true;
  }

  const useHostedUI = isFeatureFlagEnabled('HOSTED_UI');

  if (!useHostedUI) {
    return { name: 'notfound' };
  }
}

// ─── Route Access Gates ────────────────────────────────────────────────
//
// Routes are protected by `beforeEnter` guards and `meta` properties.
// The `validatePermission` guard calls `hasAccess(requiredSubscription, allowedRoles)`
// from useNavigation.js, which checks the user's JWT claims against the route meta.
//
// Available meta gates:
//
//   allowedRoles: ['MGR', 'SUP']
//     Restricts access by Cognito group. User must belong to at least one
//     listed role. Roles: ADM (admin), REP (contributor), SUP (supporter), MGR (leader).
//     Child routes inherit the parent's allowedRoles if they don't define their own.
//
//   requiredSubscription: ['premium']
//     Restricts access by the `auxo:subs` JWT claim. User must have at least
//     one matching subscription. If the claim is absent from the JWT entirely,
//     access is allowed (graceful degradation for pre-subscription rollout).
//
//   featureFlag: 'LEADER_HUB'
//     Hides the route entirely when the flag is disabled. Checked before
//     role/subscription gates.
//
// Gates can be combined — all must pass:
//
//   meta: {
//     allowedRoles: ['MGR'],
//     requiredSubscription: ['analytics_plus'],
//     featureFlag: 'ANALYTICS_V2',
//   }
//
// Sidebar visibility uses the same gates. Routes with `showInSidebar: true`
// are filtered by `buildNavTree()` in useNavigation.js, which calls `hasAccess()`
// and `isFeatureFlagEnabled()` — so gated routes are automatically hidden from
// the nav for users who can't access them.
//
// Tab bar items (the top tabs within a page) support the same gates. Individual
// tabs in `tabBarItems` can specify `allowedRoles` and/or `requiredSubscription`.
// `useTabBarItems()` in useNavigation.js filters them via `hasAccess()`, so
// gated tabs are hidden for users who don't qualify.
//
//   tabBarItems: [
//     { buttonText: 'Overview', route: '/stats/ar', testId: 'tab-Overview' },
//     { buttonText: 'Admin Only', route: '/stats/ar/admin', testId: 'tab-Admin',
//       allowedRoles: ['ADM'] },
//     { buttonText: 'Premium', route: '/stats/ar/premium', testId: 'tab-Premium',
//       requiredSubscription: ['premium'] },
//   ]
// ───────────────────────────────────────────────────────────────────────

export const routes = [
  {
    path: '/',
    redirect: '/lead',
  },
  {
    path: '/status',
    redirect: '/lead',
  },
  {
    path: '/login',
    name: 'login',
    beforeEnter: [useHostedUI],
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/LogoutView.vue'),
  },
  {
    path: '/leader-hub',
    name: 'leaderHub',
    redirect: '/leader-hub/productivity',
    component: () => import('@/views/LeaderHubView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'Leader Hub',
      navIcon: 'sidebar-leader-hub',
      order: 5,
      allowedRoles: ['MGR', 'SUP'],
      testId: 'sidebar-dropdown-leaderhub',
      featureFlag: 'LEADER_HUB',
    },
    children: [
      {
        path: 'productivity',
        name: 'productivity',
        meta: {
          showInSidebar: true,
          navLabel: 'Productivity',
          order: 10,
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/leader-hub/productivity',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Detail',
              route: '/leader-hub/productivity/details',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: '',
            name: 'productivityOverview',
            meta: {
              pageTitle: 'Productivity Overview',
            },
            component: () =>
              import('@hub/productivity/ProductivityOverviewTab.vue'),
          },
          {
            path: 'details',
            name: 'productivityDetails',
            meta: {
              pageTitle: 'Productivity Detail',
            },
            component: () =>
              import('@hub/productivity/ProductivityDetailsTab.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/ai-advisor',
    name: 'aiAdvisor',
    component: () => import('@/views/AiAdvisorView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'AI Advisor',
      navIcon: 'sidebar-ai-advisor',
      order: 7,
      allowedRoles: ['MGR', 'SUP'],
      testId: 'sidebar-item-ai-advisor',
      featureFlag: 'AI_ADVISOR',
      pageTitle: 'AI Advisor',
    },
  },
  {
    path: '/lead',
    name: 'lead',
    component: () => import('@/views/HighFiveView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'High Five',
      navIcon: 'sidebar-high-five',
      order: 10,
      allowedRoles: ['ADM', 'REP', 'SUP', 'MGR'],
      testId: 'sidebar-item-lead',
    },
    children: [
      {
        path: '',
        name: 'leadDefault',
        meta: {
          pageTitle: 'My Work',
          tabBarItems: [
            { buttonText: 'My Work', route: '/lead', testId: 'tab-My Work' },
            {
              buttonText: 'Recent Items',
              route: '/lead/recent',
              testId: 'tab-Recent Items',
            },
          ],
        },
        component: () => import('@lead/tabs/LeadTab.vue'),
      },
      {
        path: 'recent',
        name: 'recent',
        meta: {
          pageTitle: 'Recent Items',
          tabBarItems: [
            { buttonText: 'My Work', route: '/lead', testId: 'tab-My Work' },
            {
              buttonText: 'Recent Items',
              route: '/lead/recent',
              testId: 'tab-Recent Items',
            },
          ],
        },
        component: () => import('@lead/tabs/RecentTab.vue'),
      },
      {
        path: ':id(\\d+)', // numeric only — rejects non-numeric work item IDs before guards run
        name: 'leadId',
        component: () => import('@lead/tabs/WorkItemDetailTab.vue'),
      },
    ],
  },
  {
    path: '/document/:claim(\\d+)', // numeric only — rejects non-numeric claim IDs before guards run
    name: 'document',
    meta: {
      allowedRoles: ['MGR', 'REP', 'ADM'],
      pageTitle: 'Appeal Letter',
      pageSubtitle: 'Customize your document below',
    },
    component: () => import('@/views/DocumentView.vue'),
    beforeEnter: [validateAuth, validatePermission],
  },
  {
    path: '/gameplan',
    name: 'gameplan',
    component: () => import('@/views/GamePlanView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'Game Plan',
      navIcon: 'sidebar-gameplan',
      order: 20,
      allowedRoles: ['MGR', 'SUP'],
      testId: 'sidebar-item-gameplan',
      tabBarItems: [
        { buttonText: 'Overview', route: '/gameplan', testId: 'tab-Overview' },
        {
          buttonText: 'Worklists',
          route: '/gameplan/worklists',
          testId: 'tab-Worklists',
        },
        {
          buttonText: 'Inflow & Outflow Details',
          route: '/gameplan/details',
          testId: 'tab-Inflow & Outflow Details',
        },
      ],
    },
    children: [
      {
        path: '',
        name: 'gameplanOverview',
        meta: {
          pageTitle: 'Game Plan Overview',
        },
        component: () => import('@gameplan/tabs/GamePlanOverviewTab.vue'),
      },
      {
        path: 'worklists',
        name: 'gameplanWorklists',
        meta: {
          pageTitle: 'Worklists',
        },
        component: () => import('@gameplan/tabs/GamePlanWorklistsTab.vue'),
      },
      {
        path: 'worklists/new',
        name: 'newWorklist',
        meta: {
          pageTitle: 'Create Worklist',
        },
        component: () => import('@gameplan/worklists/UpsertWorkList.vue'),
      },
      {
        path: 'worklists/edit',
        name: 'editWorklist',
        meta: {
          pageTitle: 'Edit Worklist',
        },
        component: () => import('@gameplan/worklists/UpsertWorkList.vue'),
      },
      {
        path: 'details',
        name: 'gameplanDetails',
        meta: {
          pageTitle: 'Inflow & Outflow Details',
        },
        component: () => import('@gameplan/tabs/GamePlanDetailsTab.vue'),
      },
    ],
  },
  {
    path: '/users',
    name: 'users',
    meta: {
      showInSidebar: true,
      navLabel: 'Users',
      navIcon: 'sidebar-users',
      order: 30,
      allowedRoles: ['SUP', 'MGR'],
      testId: 'sidebar-item-users',
      pageTitle: 'Users',
    },
    component: () => import('@/views/UsersView.vue'),
    beforeEnter: [validateAuth, validatePermission],
  },
  {
    path: '/users/:auxo_user_id(\\d+)', // numeric only — rejects non-numeric user IDs before guards run
    name: 'userDetail',
    component: () => import('@/views/UserDetailsView.vue'),
    beforeEnter: [validateAuth, validatePermission, validateUserProfileAccess],
    meta: {
      tabBarItems: [
        {
          buttonText: 'Profile',
          route: '/users/:auxo_user_id',
          testId: 'tab-Profile',
        },
        {
          buttonText: 'Badges',
          route: '/users/:auxo_user_id/achievements',
          testId: 'tab-Badges',
        },
      ],
    },
    children: [
      {
        path: '',
        name: 'userProfile',
        component: () => import('@users/profile/UserProfile.vue'),
      },
      {
        path: 'achievements',
        name: 'userAchievements',
        meta: {
          pageTitle: 'Badges',
        },
        component: () => import('@users/achievements/UserAchievements.vue'),
      },
    ],
  },
  {
    path: '/stats',
    name: 'stats',
    component: () => import('@/views/StatsView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInSidebar: true,
      navLabel: 'Analytics',
      navIcon: 'sidebar-analytics',
      order: 40,
      allowedRoles: ['MGR'],
      testId: 'sidebar-dropdown-analytics',
    },
    children: [
      {
        path: 'ar',
        name: 'ar',
        meta: {
          showInSidebar: true,
          navLabel: 'AR',
          order: 10,
          allowedRoles: ['MGR'],
          statType: 'ar',
          store: 'arStatsStore',
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/ar',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/ar/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'High Dollar',
              route: '/stats/ar/high-dollar',
              testId: 'tab-High Dollar',
            },
            {
              buttonText: 'Detail',
              route: '/stats/ar/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: '',
            name: 'arOverview',
            meta: {
              pageTitle: 'AR Overview',
              pageSubtitle:
                'Average calculation does not include current month. Balance Filter does not apply to metrics displayed on this page.',
            },
            component: () => import('@stats/ar/ArOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'arBreakout',
            meta: {
              pageTitle: 'AR Breakout',
            },
            component: () => import('@stats/ar/ArBreakout.vue'),
          },
          {
            path: 'high-dollar',
            name: 'arHighDollar',
            meta: {
              pageTitle: 'AR High Dollar',
            },
            component: () => import('@stats/ar/ArHighDollar.vue'),
          },
          {
            path: 'detail',
            name: 'arDetail',
            meta: {
              pageTitle: 'AR Detail',
            },
            component: () => import('@stats/ar/ArDetail.vue'),
          },
        ],
      },
      {
        path: 'denials',
        name: 'denials',
        meta: {
          showInSidebar: true,
          navLabel: 'Denials',
          order: 20,
          allowedRoles: ['MGR'],
          statType: 'denials',
          store: 'denialsStore',
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/denials',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/denials/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'Detail',
              route: '/stats/denials/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: '',
            name: 'denialsOverview',
            meta: {
              pageTitle: 'Denials Overview',
            },
            component: () => import('@stats/denials/DenialsOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'denialsBreakout',
            meta: {
              pageTitle: 'Denials Breakout',
            },
            component: () => import('@stats/denials/DenialsBreakout.vue'),
          },
          {
            path: 'detail',
            name: 'denialsDetail',
            meta: {
              pageTitle: 'Denials Detail',
            },
            component: () => import('@stats/denials/DenialsDetail.vue'),
          },
        ],
      },
      {
        path: 'workforce-management/activity',
        name: 'activity',
        meta: {
          showInSidebar: true,
          navLabel: 'Activity',
          order: 30,
          allowedRoles: ['MGR'],
          statType: 'work_activity',
          store: 'activityStatsStore',
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/workforce-management/activity',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/workforce-management/activity/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'Detail',
              route: '/stats/workforce-management/activity/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: '',
            name: 'activityOverview',
            meta: {
              pageTitle: 'Activity Overview',
              pageSubtitle:
                'Data displayed includes the "Complete" action type by default. This can be changed in the advanced filter menu.',
            },
            component: () => import('@stats/activity/ActivityOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'activityBreakout',
            meta: {
              pageTitle: 'Activity Breakout',
            },
            component: () => import('@stats/activity/ActivityBreakout.vue'),
          },
          {
            path: 'detail',
            name: 'activityDetail',
            meta: {
              pageTitle: 'Activity Detail',
            },
            component: () => import('@stats/activity/ActivityDetail.vue'),
          },
        ],
      },
      {
        path: 'workforce-management/gap',
        name: 'workGap',
        meta: {
          showInSidebar: true,
          navLabel: 'Work Gap',
          order: 40,
          allowedRoles: ['MGR'],
          statType: 'work_gap',
          store: 'workGapStore',
          tabBarItems: [
            {
              buttonText: 'Overview',
              route: '/stats/workforce-management/gap',
              testId: 'tab-Overview',
            },
            {
              buttonText: 'Breakout',
              route: '/stats/workforce-management/gap/breakout',
              testId: 'tab-Breakout',
            },
            {
              buttonText: 'Detail',
              route: '/stats/workforce-management/gap/detail',
              testId: 'tab-Detail',
            },
          ],
        },
        children: [
          {
            path: '',
            name: 'workGapOverview',
            meta: {
              pageTitle: 'Work Gap Overview',
            },
            component: () => import('@stats/workgap/WorkGapOverview.vue'),
          },
          {
            path: 'breakout',
            name: 'workGapBreakout',
            meta: {
              pageTitle: 'Work Gap Breakout',
            },
            component: () => import('@stats/workgap/WorkGapBreakout.vue'),
          },
          {
            path: 'detail',
            name: 'workGapDetail',
            meta: {
              pageTitle: 'Work Gap Detail',
            },
            component: () => import('@stats/workgap/WorkGapDetail.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/devology',
    name: 'devology',
    redirect: '/devology/sandbox',
    component: () => import('@/views/DevologyView.vue'),
    beforeEnter: [validateAuth, validateNonProd],
    meta: {
      showInSidebar: true,
      navLabel: 'Devology',
      navIcon: 'sidebar-devology',
      order: 45,
      allowedRoles: ['ADM', 'REP', 'SUP', 'MGR'],
      testId: 'sidebar-item-devology',
      nonProdOnly: true,
      hiddenByDefault: true,
      tabBarItems: [
        {
          buttonText: 'Sandbox',
          route: '/devology/sandbox',
          testId: 'tab-Sandbox',
        },
      ],
    },
    children: [
      {
        path: 'sandbox',
        name: 'devologySandbox',
        meta: {
          pageTitle: 'Devology Sandbox',
        },
        component: () =>
          import(
            '@/components/features/devology/DevologySandboxTab.vue'
          ),
      },
    ],
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    beforeEnter: [validateAuth, validatePermission],
    meta: {
      showInFooter: true,
      navLabel: 'Settings',
      navIcon: 'sidebar-settings',
      order: 50,
      allowedRoles: ['ADM'],
      testId: 'sidebar-item-settings',
      tabBarItems: [
        {
          buttonText: 'Admin Settings',
          route: '/settings',
          testId: 'tab-Admin Settings',
        },
        {
          buttonText: 'User Management',
          route: '/settings/user-management',
          testId: 'tab-User Management',
        },
      ],
    },
    children: [
      {
        path: '',
        name: 'reportAnalytics',
        meta: {
          pageTitle: 'Admin Settings',
        },
        component: () =>
          import('@/components/features/stats/ReportAnalyticsSettings.vue'),
      },
      {
        path: 'user-management',
        name: 'userManagement',
        meta: {
          pageTitle: 'User Management',
        },
        component: () =>
          import('@/components/features/user_mgmt/AdminPanelSettings.vue'),
      },
    ],
  },
  {
    name: 'notfound',
    path: '/notfound',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 404,
      errorTitle: 'Page Not Found',
      errorMessage: "This page doesn't exist.",
      showHomeButton: true,
    },
  },
  {
    name: 'forbidden',
    path: '/forbidden',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 403,
      errorTitle: 'Access Denied',
      errorMessage: "You don't have permission to access this resource.",
      showHomeButton: true,
    },
  },
  {
    name: 'unauthorized',
    path: '/unauthorized',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 401,
      errorTitle: 'Authentication Required',
      errorMessage: 'Please log in to continue.',
      showHomeButton: false,
      showSignInButton: true,
    },
  },
  {
    name: 'catchAll',
    path: '/:catchAll(.*)', // matches any unmatched URL — catch-all 404 fallback
    beforeEnter: [validateAuth],
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      errorCode: 404,
      errorTitle: 'Page Not Found',
      errorMessage: "This page doesn't exist.",
      showHomeButton: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async to => {
  if (to.path === '/login' || to.path === '/logout') {
    const { useWebSockets } = await import('@/stores/useWebSockets');
    const ws_store = useWebSockets();
    if (ws_store.checkConnection) {
      await ws_store.close();
    }
  }
});

router.beforeEach(async (to, from) => {
  if (
    from.path === '/' &&
    to.path === '/lead' &&
    to?.redirectedFrom?.path === '/status' &&
    to?.query?.code
  ) {
    try {
      await getTokens(to.query.code, null);
    } catch (err) {
      console.error('Token retrieval failed:', err);
      return { name: 'unauthorized' };
    }
  }
});
export default router;
